import "./style.scss";
import "draft-js/dist/Draft.css";

import FormatBold from "@mui/icons-material/FormatBold";
import FormatItalic from "@mui/icons-material/FormatItalic";
import FormatStrikethrough from "@mui/icons-material/FormatStrikethrough";
import FormatUnderlined from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { IconButton } from "@mui/material";
import { Heading2 } from "assets/img/svg/heading2";
import InsertVariable from "components/shared/InsertVariableMenu";
import type { ContentState } from "draft-js";
import { convertFromRaw, Editor, EditorState, Modifier, RichUtils } from "draft-js";
import { stateFromHTML } from "draft-js-import-html";
import { getCleanJSON } from "helpers/utils";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "store/store";

import defaultEmail from "./defaultEmail";

const inlineStyles = [
    { style: "BOLD", icon: <FormatBold /> },
    { style: "ITALIC", icon: <FormatItalic /> },
    { style: "UNDERLINE", icon: <FormatUnderlined /> },
    { style: "STRIKETHROUGH", icon: <FormatStrikethrough /> },
];

const customStyleMap = {
    STRIKETHROUGH: {
        textDecoration: "line-through",
    },
};

interface MailEditorProps {
    onSave: (value: ContentState) => void;
    onChange: (value: boolean) => void;
    hasChanged: boolean;
    languageTab: "en" | "fr";
}

const MailEditor = ({ onSave, hasChanged, onChange, languageTab }: MailEditorProps) => {
    const [t] = useTranslation();

    // Create Content State from string with HTML tags
    const defaultState = stateFromHTML(defaultEmail);

    // To set up the editor
    const [editorState, setEditorState] = useState(EditorState.createWithContent(defaultState));
    const editorRef: any = useRef(null);
    const [isFocused, setIsFocused] = useState(false);

    // Info from store to get contract saved
    const { companySettings } = useSelector((state: RootState) => state.settings);
    const [settings, setSettings] = useState(companySettings?.settings);

    const handleInlineStyle = (style: string) => {
        setEditorState(RichUtils.toggleInlineStyle(editorState, style));
        onChange(true);
    };

    const onEditorChange = (e: any) => {
        const currentPlainText = editorState.getCurrentContent().getPlainText();
        const newPlainText = e.getCurrentContent().getPlainText();
        setEditorState(e);

        if (currentPlainText.trim() !== newPlainText.trim()) {
            onChange(true);
        }
    };

    useEffect(() => {
        if (hasChanged) {
            // console.log(editorState.getCurrentInlineStyle().has('UNDERLINE'))
            onSave(editorState.getCurrentContent());
        }
    });

    const handleBlur = (e: any) => {
        const currentTarget = e.currentTarget;
        setTimeout(() => {
            if (!currentTarget.contains(document.activeElement)) {
                setIsFocused(false);
            }
        }, 0);
    };

    // These two const are for the List button
    const handleToggleButtonList = (type: string) => {
        type === "bullet" && setEditorState(RichUtils.toggleBlockType(editorState, "unordered-list-item"));
        type === "h2" && setEditorState(RichUtils.toggleBlockType(editorState, "header-two"));
        onChange(true);
    };

    const handleColorListButton = (type: string) => {
        const blockType = type === "bullet" ? "unordered-list-item" : "header-two";
        const currentSelection = editorState.getSelection();
        const currentKey = currentSelection.getStartKey();
        const currentBlock = editorState.getCurrentContent().getBlockForKey(currentKey);
        const currentBlockType = currentBlock.getType();
        return currentBlockType === blockType ? "primary" : "default";
    };

    useEffect(() => {
        const mailTemplate = structuredClone(
            (Object as any)
                .values(settings)
                .find(
                    (template: { id?: number; name: any; content: any }) =>
                        template.name === `portal_welcome_email_template_${languageTab}`,
                ),
        );

        const savedMailBlocks = getCleanJSON(mailTemplate.content).blocks;
        let savedMailState: ContentState | null = null;
        if (!savedMailBlocks || savedMailBlocks === "") {
            savedMailState = stateFromHTML(
                getCleanJSON(mailTemplate.content).body.replace(/&lt;/g, "<").replace(/&gt;/g, ">"),
            );
        } else {
            savedMailState = convertFromRaw(savedMailBlocks);
        }

        const different = !Object.is(savedMailState, defaultState);

        // If ContentState saved different from default set editorState to what is saved
        if (different) {
            setEditorState(EditorState.push(editorState, savedMailState, "remove-range"));
        }

        onChange(false);
    }, [settings, languageTab]);

    /**
     * Insert text to Editor, reused in handleInserVariable();
     */
    const insertText = (text: string, editorState: any) => {
        const currentContent = editorState.getCurrentContent(),
            currentSelection = editorState.getSelection();

        const newContent = Modifier.replaceText(currentContent, currentSelection, text);
        const newEditorState = EditorState.push(editorState, newContent, "insert-characters");
        return EditorState.forceSelection(newEditorState, newContent.getSelectionAfter());
    };

    const handleInsertVariable = (placeholder: string) => {
        setEditorState(insertText(placeholder, editorState));
        onChange(true);
    };

    const variables = {
        "{{firstname}}": t("RichEditor.Variables.BeneficiaryFirstname"),
        "{{lastname}}": t("RichEditor.Variables.BeneficiaryLastname"),
    };

    return (
        <>
            <div className="mail-editor" onBlur={handleBlur}>
                <div className="textModifiers">
                    <div className="inlineButtons">
                        {inlineStyles.map((item, idx) => (
                            <IconButton
                                key={idx}
                                onClick={() => handleInlineStyle(item.style)}
                                color={editorState.getCurrentInlineStyle().has(item.style) ? "primary" : "default"}
                            >
                                {item.icon}
                            </IconButton>
                        ))}
                        {/* <IconButton
                            color={editorState.getCurrentInlineStyle().has("left") ? "primary" : "default"}
                            // onClick={() => applyAlignment("left")}
                        >
                            <FormatAlignLeftIcon />
                        </IconButton>
                        <IconButton
                            color={editorState.getCurrentInlineStyle().has("center") ? "primary" : "default"}
                            // onClick={() => applyAlignment("center")}
                        >
                            <FormatAlignCenterIcon />
                        </IconButton> */}
                        <IconButton
                            onClick={() => handleToggleButtonList("bullet")}
                            color={handleColorListButton("bullet")}
                        >
                            <FormatListBulletedIcon />
                        </IconButton>
                        <IconButton onClick={() => handleToggleButtonList("h2")} color={handleColorListButton("h2")}>
                            <Heading2 />
                        </IconButton>
                    </div>
                    <InsertVariable
                        variables={variables}
                        onSelect={(placeholder: string) => handleInsertVariable(placeholder)}
                        field=""
                    />
                </div>
                <div className="textEditor" style={(!isFocused && { minHeight: 0 }) || {}}>
                    <Editor
                        editorState={editorState}
                        onChange={onEditorChange}
                        onFocus={() => setIsFocused(true)}
                        customStyleMap={customStyleMap}
                        placeholder={t("RichEditor.StartWriting")}
                        ref={editorRef}
                    />
                </div>
            </div>
            {/* {isDirty && (
                <div className="settings-contracts-save">
                    <StyledButton
                        className="contract-save-btn"
                        onClick={() => handleEditorSave()}
                    >
                        {t("ContractSettings.Save")}
                    </StyledButton>
                </div>
            )} */}
        </>
    );
};

export default MailEditor;
